/**
 * Advanced ATS analysis engine for plain-text CVs.
 *
 * Models how real Applicant Tracking Systems (Workday, SAP SuccessFactors,
 * Greenhouse, PNet, Careers24) parse and rank a CV:
 *  - parse safety (layout artefacts, glyphs, tables, headers/footers)
 *  - contact block completeness
 *  - standard section headers
 *  - experience structure + chronology
 *  - bullet quality (power verbs, length, quantification)
 *  - keyword coverage vs target role and job spec (with gap list)
 *  - recruiter-readability and cliché / pronoun penalties
 *  - South African hygiene (POPIA — no ID numbers, no photos, no marital status)
 *
 * Deliberately dependency-free and pure so it can run in the browser, on the
 * server, and inside unit tests.
 */

import { buildDiagnostics, type AtsDiagnostics } from "@/lib/ats-diagnostics";
import { normaliseCvText } from "@/lib/cv-text-normalise";

export type AtsSeverity = "critical" | "high" | "medium" | "low";


export type AtsCheckResult = {
  id: string;
  label: string;
  passed: boolean;
  weight: number;
  /** 0..1 partial credit; passed === (partial >= 1) for binary checks. */
  partial: number;
  severity: AtsSeverity;
  category: "parse" | "contact" | "structure" | "impact" | "keywords" | "hygiene";
  detail?: string;
  fix?: string;
};

export type AtsDimensionId =
  | "formatting"
  | "keywords"
  | "grammar"
  | "readability"
  | "achievements"
  | "recruiterAppeal"
  | "completeness"
  | "professionalism";

export type AtsDimension = {
  id: AtsDimensionId;
  label: string;
  score: number;
  /** Check ids that feed this dimension. */
  checkIds: string[];
  blurb: string;
};

export type AtsVerdict = {
  band: "excellent" | "good" | "needs-improvement" | "high-risk";
  label: string;
  explanation: string;
  topActions: string[];
};

export type AtsAnalysis = {
  score: number;
  band: "poor" | "fair" | "good" | "excellent";
  checks: AtsCheckResult[];
  wordCount: number;
  bulletCount: number;
  /** Keywords from the job spec / role that are missing from the CV. */
  missingKeywords: string[];
  matchedKeywords: string[];
  /** Highest-leverage fixes, ordered by score impact. */
  priorityFixes: { label: string; fix: string; points: number }[];
  /** Eight sub-scores, so users see *where* the points went. */
  dimensions: AtsDimension[];
  /** Plain-English verdict band. */
  verdict: AtsVerdict;
  /** Non-scoring warnings surfaced from the deep diagnostics. */
  warnings: string[];
  /** Full descriptive breakdown of the document. */
  diagnostics: AtsDiagnostics;
};


export const POWER_VERBS = [
  "led","managed","drafted","implemented","reduced","increased","delivered",
  "negotiated","developed","designed","created","coordinated","launched",
  "streamlined","improved","achieved","trained","advised","reviewed","owned",
  "built","optimised","optimized","automated","secured","resolved","grew",
  "cut","saved","supervised","authored","facilitated","spearheaded","directed",
  "administered","analysed","analyzed","compiled","prepared","presented",
  "researched","monitored","mitigated","audited","reconciled","processed",
  "supported","assisted","maintained","executed","initiated","established",
];

const SECTION_HEADERS = [
  "summary","profile","objective","experience","work experience","employment",
  "education","skills","core skills","qualifications","certifications",
  "references","achievements","projects","languages","contact",
];

/** Filler that reads as noise to both ATS keyword matching and recruiters. */
const CLICHES = [
  "hard working","hard-working","team player","go-getter","think outside the box",
  "self-motivated","results-driven individual","dynamic individual","people person",
  "detail oriented person","excellent communication skills","fast learner",
  "passionate about","synergy","best of breed","hit the ground running",
];

/** Template phrasing that makes a CV interchangeable with anyone else's. */
const GENERIC_PHRASES = [
  "responsible for","duties included","various tasks","day-to-day operations",
  "wide range of","assisted with general","general office duties",
  "results-driven professional","dynamic self-starter","proven track record",
  "strong work ethic","good communication skills","willing to learn",
  "seeking a challenging position","utilise my skills","in a reputable company",
  "worked on various","helped the team","other duties as assigned",
];


const PRONOUNS = /\b(i|me|my|mine|myself)\b/gi;

/** Characters that survive a copy-paste but break ATS text extraction. */
const RISKY_GLYPHS = /[\u2022\u25AA\u25CF\u00B7\u2013\u2014]/; // allowed, but tracked
const BROKEN_GLYPHS = /[\uFFFD\u2028\u2029]|\(cid:\d+\)/;

const STOPWORDS = new Set([
  "and","the","for","with","from","that","this","your","you","are","our","will",
  "have","has","been","must","able","work","role","must","plus","etc","other",
  "including","strong","good","using","within","across","into","their","them",
  "who","all","any","per","non","via","new","also","more","most","such",
]);

function tokenize(s: string): string[] {
  return (s.toLowerCase().match(/[a-z][a-z0-9+#./-]{2,}/g) || []).filter(
    (w) => !STOPWORDS.has(w),
  );
}

/** Split a job spec into weighted keyword phrases (commas/newlines/bullets). */
export function extractKeywords(role: string, jobSpec: string): string[] {
  const out = new Set<string>();
  const fromSpec = jobSpec
    .split(/[,;\n•\-–|]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 2 && s.length <= 48);
  for (const phrase of fromSpec) {
    // Multi-word phrases stay intact; long sentences get token-mined.
    const words = phrase.split(/\s+/);
    if (words.length <= 4) out.add(phrase);
    else for (const t of tokenize(phrase)) out.add(t);
  }
  for (const t of tokenize(role)) out.add(t);
  return Array.from(out).filter((k) => k.length > 2).slice(0, 60);
}

export function analyseCvText(
  text: string,
  role: string,
  jobSpec: string,
): AtsAnalysis {
  // Every score runs through the same repair layer, so a CV pasted from a PDF
  // is judged on its real structure instead of a flattened blob of text.
  const t = normaliseCvText(text || "");
  const lower = t.toLowerCase();
  const lines = t.split("\n").map((l) => l.trim());
  const nonEmpty = lines.filter(Boolean);
  const wordCount = t.split(/\s+/).filter(Boolean).length;

  const checks: AtsCheckResult[] = [];
  const add = (c: Omit<AtsCheckResult, "partial"> & { partial?: number }) =>
    checks.push({
      ...c,
      partial: c.partial ?? (c.passed ? 1 : 0),
      passed: c.partial !== undefined ? c.partial >= 1 : c.passed,
    });

  /* ---------------- Parse safety ---------------- */
  const brokenGlyphs = BROKEN_GLYPHS.test(t);
  const tableLike = nonEmpty.filter((l) => /\t.*\t|\|\s.*\s\|/.test(l)).length;
  const allCapsRun = nonEmpty.filter(
    (l) => l.length > 60 && l === l.toUpperCase(),
  ).length;
  const parseIssues = (brokenGlyphs ? 1 : 0) + (tableLike > 2 ? 1 : 0) + (allCapsRun > 1 ? 1 : 0);
  add({
    id: "parse",
    label: "Clean, machine-readable text layer",
    passed: parseIssues === 0,
    weight: 10,
    severity: "critical",
    category: "parse",
    detail: brokenGlyphs
      ? "Unreadable characters detected — the PDF likely used a non-embedded font or was scanned."
      : tableLike > 2
        ? `${tableLike} table/column-like lines detected. ATS flatten tables and scramble the order.`
        : allCapsRun > 1
          ? "Long ALL-CAPS paragraphs detected — parsers mis-handle them and recruiters skip them."
          : "No parsing hazards detected.",
    fix: "Export as a single-column, text-based PDF or .docx with no tables, text boxes, headers or footers.",
  });

  /* ---------------- Contact block ---------------- */
  const hasEmail = /[\w.+-]+@[\w-]+\.[\w.-]+/.test(t);
  const hasPhone = /(\+?\d[\d\s\-().]{7,}\d)/.test(t);
  const hasLocation =
    /\b(gauteng|limpopo|mpumalanga|kwazulu|natal|free state|north west|northern cape|western cape|eastern cape|johannesburg|pretoria|durban|cape town|polokwane|bloemfontein|port elizabeth|gqeberha|east london|nelspruit|mbombela|kimberley|rustenburg|south africa)\b/i.test(
      t,
    );
  const hasLinkedIn = /linkedin\.com\/in\//i.test(t);
  const contactScore =
    (hasEmail ? 0.4 : 0) + (hasPhone ? 0.35 : 0) + (hasLocation ? 0.15 : 0) + (hasLinkedIn ? 0.1 : 0);
  add({
    id: "contact",
    label: "Complete contact block (email, phone, city, LinkedIn)",
    passed: contactScore >= 0.9,
    partial: Math.min(1, contactScore),
    weight: 12,
    severity: "critical",
    category: "contact",
    detail: [
      hasEmail ? null : "no email",
      hasPhone ? null : "no phone",
      hasLocation ? null : "no city/province",
      hasLinkedIn ? null : "no LinkedIn URL",
    ]
      .filter(Boolean)
      .join(", ") || "All contact fields detected.",
    fix: "Put name, city + province, phone, professional email and LinkedIn URL on separate lines at the top — never inside a header/footer.",
  });

  /* ---------------- Section headers ---------------- */
  const foundSections = SECTION_HEADERS.filter((h) =>
    new RegExp(`(^|\\n)\\s*${h}\\b`, "i").test(lower),
  );
  const coreSections = ["experience", "education", "skills"].filter((h) =>
    foundSections.some((f) => f.includes(h)),
  );
  add({
    id: "sections",
    label: "Standard section headers (Experience, Education, Skills)",
    passed: coreSections.length === 3,
    partial: coreSections.length / 3,
    weight: 10,
    severity: "high",
    category: "structure",
    detail: `Detected: ${foundSections.slice(0, 8).join(", ") || "none"}.`,
    fix: "Use plain, conventional headers on their own line. Creative labels like 'My Journey' are not recognised.",
  });

  /* ---------------- Length ---------------- */
  const lengthOk = wordCount >= 350 && wordCount <= 1100;
  add({
    id: "length",
    label: "Length is 350–1100 words (1–2 pages)",
    passed: lengthOk,
    partial: lengthOk ? 1 : wordCount < 350 ? Math.max(0, wordCount / 350) : 0.5,
    weight: 6,
    severity: "medium",
    category: "structure",
    detail: `${wordCount} words.`,
    fix: wordCount < 350 ? "Expand each role with 3–5 achievement bullets." : "Trim older roles to 2 bullets each.",
  });

  /* ---------------- Bullets ---------------- */
  const bulletLines = nonEmpty.filter((l) => /^[-•*·▪●○]/.test(l));
  const bulletBodies = bulletLines.map((l) => l.replace(/^[-•*·▪●○]\s*/, ""));
  const goodLen = bulletBodies.filter((b) => {
    const w = b.split(/\s+/).length;
    return w >= 8 && w <= 34;
  }).length;
  const bulletRatio = bulletBodies.length ? goodLen / bulletBodies.length : 0;
  add({
    id: "bullets",
    label: "6+ concise achievement bullets (8–34 words each)",
    passed: bulletBodies.length >= 6 && bulletRatio >= 0.7,
    partial: Math.min(1, (bulletBodies.length / 6) * 0.5 + bulletRatio * 0.5),
    weight: 8,
    severity: "high",
    category: "structure",
    detail: `${bulletBodies.length} bullets, ${goodLen} within the ideal length.`,
    fix: "One achievement per bullet. Split anything over ~34 words; merge one-liners under 8 words.",
  });

  /* ---------------- Power verbs ---------------- */
  const verbLed = bulletBodies.filter((b) =>
    POWER_VERBS.some((v) => new RegExp(`^${v}\\b`, "i").test(b)),
  ).length;
  const verbRatio = bulletBodies.length ? verbLed / bulletBodies.length : 0;
  add({
    id: "verbs",
    label: "80%+ of bullets open with a power verb",
    passed: verbRatio >= 0.8 && bulletBodies.length >= 3,
    partial: bulletBodies.length >= 3 ? Math.min(1, verbRatio / 0.8) : 0,
    weight: 9,
    severity: "high",
    category: "impact",
    detail: `${Math.round(verbRatio * 100)}% of bullets are verb-led.`,
    fix: "Start every bullet with Led / Delivered / Drafted / Reduced / Coordinated — never 'Responsible for' or 'Duties included'.",
  });

  /* ---------------- Quantified impact ---------------- */
  const quantBullets = bulletBodies.filter((b) =>
    /(\bR\s?\d|\d+\s?%|\b\d{2,}\b|\b\d+\s*(years?|months?|clients?|matters?|files?|staff|people|reports?|cases?|hours?|days?)\b)/i.test(
      b,
    ),
  ).length;
  const quantRatio = bulletBodies.length ? quantBullets / bulletBodies.length : 0;
  add({
    id: "quantified",
    label: "40%+ of bullets carry a measurable result",
    passed: quantRatio >= 0.4 && quantBullets >= 3,
    partial: Math.min(1, quantRatio / 0.4),
    weight: 12,
    severity: "high",
    category: "impact",
    detail: `${quantBullets} of ${bulletBodies.length} bullets are quantified.`,
    fix: "Add real volumes, rand values, percentages, headcount or turnaround times — only figures you can actually defend.",
  });

  /* ---------------- Dates / chronology ---------------- */
  const years = (t.match(/\b(19|20)\d{2}\b/g) || []).map(Number);
  const ranges = (
    t.match(/\b(19|20)\d{2}\s*[–\-—to]+\s*((19|20)\d{2}|present|current)\b/gi) || []
  ).length;
  add({
    id: "dates",
    label: "Every role carries a month/year range",
    passed: ranges >= 2,
    partial: Math.min(1, ranges / 2),
    weight: 7,
    severity: "high",
    category: "structure",
    detail: `${ranges} date range(s) and ${years.length} year mention(s) detected.`,
    fix: "Write dates as 'Mar 2022 – Present'. Missing dates are the single most common ATS rejection trigger.",
  });

  /* ---------------- Keywords ---------------- */
  const keywords = extractKeywords(role, jobSpec);
  const matched = keywords.filter((k) => lower.includes(k));
  const missing = keywords.filter((k) => !lower.includes(k));
  const kwRatio = keywords.length ? matched.length / keywords.length : 0;
  add({
    id: "keywords",
    label: "Target-role / job-spec keyword coverage ≥ 70%",
    passed: keywords.length > 0 && kwRatio >= 0.7,
    partial: keywords.length ? Math.min(1, kwRatio / 0.7) : 0,
    weight: 16,
    severity: "critical",
    category: "keywords",
    detail: keywords.length
      ? `Matched ${matched.length}/${keywords.length}. Missing: ${missing.slice(0, 10).join(", ") || "—"}.`
      : "Add the target role (and ideally paste the job advert) to measure keyword coverage.",
    fix: "Mirror the advert's exact wording in your summary, skills line and bullets — but only for experience you genuinely have.",
  });

  /* ---------------- Skills line ---------------- */
  const skillsBlock = /(^|\n)\s*(core skills|skills|key skills|technical skills)\b[^\n]*\n?([^\n]*)/i.exec(t);
  const skillCount = skillsBlock
    ? (skillsBlock[3] || "").split(/[,•|]/).map((s) => s.trim()).filter((s) => s.length > 1).length
    : 0;
  add({
    id: "skills",
    label: "Dedicated skills line with 8+ keywords",
    passed: skillCount >= 8,
    partial: Math.min(1, skillCount / 8),
    weight: 6,
    severity: "medium",
    category: "keywords",
    detail: skillCount ? `${skillCount} skills detected on the skills line.` : "No dedicated skills line found.",
    fix: "Add a 'CORE SKILLS' line with 8–14 comma-separated, role-relevant keywords.",
  });

  /* ---------------- Hygiene: clichés, pronouns, POPIA ---------------- */
  const clicheHits = CLICHES.filter((c) => lower.includes(c));
  const pronounHits = (t.match(PRONOUNS) || []).length;
  add({
    id: "tone",
    label: "No clichés or first-person pronouns",
    passed: clicheHits.length === 0 && pronounHits <= 2,
    partial: clicheHits.length === 0 && pronounHits <= 2 ? 1 : clicheHits.length <= 1 && pronounHits <= 6 ? 0.5 : 0,
    weight: 4,
    severity: "low",
    category: "hygiene",
    detail: [
      clicheHits.length ? `Clichés: ${clicheHits.slice(0, 4).join(", ")}` : null,
      pronounHits > 2 ? `${pronounHits} first-person pronouns` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "Tone is clean.",
    fix: "Replace 'hard working team player' with an evidenced achievement. Drop 'I / my' — CVs are written in implied first person.",
  });

  const popiaRisk =
    /\b\d{13}\b/.test(t.replace(/\s/g, "")) ||
    /\b(marital status|date of birth|religion|race|id\s*(no|number)\s*[:#])/i.test(t);
  add({
    id: "popia",
    label: "No unnecessary personal data (POPIA-safe)",
    passed: !popiaRisk,
    weight: 4,
    severity: "medium",
    category: "hygiene",
    detail: popiaRisk
      ? "ID number, date of birth, marital status or similar detected — remove it."
      : "No sensitive personal identifiers detected.",
    fix: "SA employers must not require ID numbers, photos, marital status or date of birth on a CV. Remove them.",
  });

  /* ---------------- Summary ---------------- */
  const summaryMatch = /(^|\n)\s*(professional summary|summary|profile)\b[^\n]*\n([\s\S]{0,900}?)(\n\s*\n|\n[A-Z][A-Z ]{3,}\n)/i.exec(
    t + "\n\n",
  );
  const summaryWords = summaryMatch ? summaryMatch[3].split(/\s+/).filter(Boolean).length : 0;
  add({
    id: "summary",
    label: "Targeted professional summary (45–110 words)",
    passed: summaryWords >= 45 && summaryWords <= 110,
    partial: summaryWords ? Math.min(1, summaryWords / 45) : 0,
    weight: 6,
    severity: "medium",
    category: "structure",
    detail: summaryWords ? `${summaryWords}-word summary detected.` : "No professional summary section found.",
    fix: "Open with 3–4 sentences naming the target role, years of experience and two evidenced strengths.",
  });

  /* ---------------- Deep diagnostics (descriptive) ---------------- */
  const diagnostics = buildDiagnostics(t, role, POWER_VERBS);

  /* ---------------- Grammar & mechanics ---------------- */
  const grammarIssues = diagnostics.grammar.issues.length;
  add({
    id: "grammar",
    label: "Grammar, punctuation and mechanics are clean",
    passed: grammarIssues === 0,
    partial: Math.max(0, 1 - grammarIssues * 0.22),
    weight: 6,
    severity: "medium",
    category: "hygiene",
    detail:
      diagnostics.grammar.issues.map((i) => i.type).join(", ") ||
      "No grammar or mechanics issues detected.",
    fix: "Fix doubled words, spaces before punctuation, lowercase line starts and over-long sentences, then re-scan.",
  });

  /* ---------------- Recruiter readability ---------------- */
  const r = diagnostics.readability;
  const readOk = r.bulletDensity >= 30 && r.longestParagraphWords <= 90 && r.scanSeconds <= 30;
  add({
    id: "readability",
    label: "Scannable in under 30 seconds (bullet-led, short paragraphs)",
    passed: readOk,
    partial:
      (r.bulletDensity >= 30 ? 0.4 : (r.bulletDensity / 30) * 0.4) +
      (r.longestParagraphWords <= 90 ? 0.3 : 0.1) +
      (r.scanSeconds <= 30 ? 0.3 : 0.1),
    weight: 6,
    severity: "medium",
    category: "structure",
    detail: `${r.bulletDensity}% bullet lines, longest paragraph ${r.longestParagraphWords} words, ~${r.scanSeconds}s to skim.`,
    fix: "Convert dense paragraphs into bullets and keep every paragraph under about 90 words.",
  });

  /* ---------------- ATS risk detector ---------------- */
  const criticalRisks = diagnostics.risks.flags.filter((f) => f.severity === "critical").length;
  const otherRisks = diagnostics.risks.flags.length - criticalRisks;
  add({
    id: "risk",
    label: "No layout artefacts that break ATS parsing",
    passed: diagnostics.risks.flags.length === 0,
    partial: Math.max(0, 1 - criticalRisks * 0.5 - otherRisks * 0.2),
    weight: 8,
    severity: "critical",
    category: "parse",
    detail:
      diagnostics.risks.flags.map((f) => f.label).join(", ") ||
      "No tables, columns, emojis, graphics or header/footer content detected.",
    fix: "Rebuild as a single-column document: no tables, text boxes, icons, images, headers or footers.",
  });

  /* ---------------- Completeness ---------------- */
  const wantedSections = ["professional summary", "experience", "education", "skills"];
  const haveSections = wantedSections.filter((s) => diagnostics.sections.found.includes(s)).length;
  const extras = ["certifications", "languages", "achievements", "references"].filter((s) =>
    diagnostics.sections.found.includes(s),
  ).length;
  add({
    id: "completeness",
    label: "All expected CV sections present",
    passed: haveSections === 4 && extras >= 2,
    partial: Math.min(1, (haveSections / 4) * 0.75 + (extras / 4) * 0.25),
    weight: 7,
    severity: "high",
    category: "structure",
    detail: `${haveSections}/4 core sections and ${extras}/4 supporting sections detected.`,
    fix: `Add the missing sections: ${[...diagnostics.sections.missing, ...diagnostics.sections.recommended].slice(0, 5).join(", ") || "—"}.`,
  });

  /* ---------------- Date hygiene ---------------- */
  const dateProblems =
    diagnostics.dates.formatIssues.length +
    diagnostics.dates.futureDates.length +
    diagnostics.dates.overlaps.length +
    diagnostics.dates.gaps.length;
  add({
    id: "date-quality",
    label: "Dates are consistent, complete and gap-free",
    passed: dateProblems === 0 && diagnostics.dates.ranges.length > 0,
    partial: diagnostics.dates.ranges.length
      ? Math.max(0, 1 - dateProblems * 0.2)
      : 0,
    weight: 5,
    severity: "medium",
    category: "structure",
    detail:
      [
        diagnostics.dates.gaps.length ? `${diagnostics.dates.gaps.length} employment gap(s)` : null,
        diagnostics.dates.overlaps.length ? `${diagnostics.dates.overlaps.length} overlapping role(s)` : null,
        diagnostics.dates.futureDates.length ? "future date(s)" : null,
        diagnostics.dates.formatIssues.length ? "inconsistent date format" : null,
      ]
        .filter(Boolean)
        .join(", ") || `${diagnostics.dates.ranges.length} clean date range(s).`,
    fix: "Use 'Mon YYYY – Mon YYYY' everywhere and add a one-line explanation for any gap longer than six months.",
  });

  /* ---------------- Contact detail quality ---------------- */
  add({
    id: "contact-quality",
    label: "Professional, unambiguous contact details",
    passed: diagnostics.contact.warnings.length === 0,
    partial: Math.max(0, 1 - diagnostics.contact.warnings.length * 0.25),
    weight: 4,
    severity: "medium",
    category: "contact",
    detail: diagnostics.contact.warnings[0] || "Contact block is clean and professional.",
    fix: "One professional email, one reachable phone number, your city and province, and a LinkedIn URL.",
  });

  /* ---------------- Weak-language detector ---------------- */
  const weakBullets = diagnostics.bullets.filter((b) => b.weakOpener).length;
  add({
    id: "weak-language",
    label: "No duty-listing language ('responsible for', 'worked on')",
    passed: weakBullets === 0,
    partial: diagnostics.bullets.length
      ? Math.max(0, 1 - weakBullets / diagnostics.bullets.length)
      : 1,
    weight: 5,
    severity: "high",
    category: "impact",
    detail: weakBullets
      ? `${weakBullets} bullet(s) open with duty-listing phrases.`
      : "Every bullet is written as an achievement, not a duty.",
    fix: "Rewrite duty statements as outcomes: what you did, how, and what changed as a result.",
  });

  /* ---------------- Specificity (anti-generic) detector ---------------- */
  const genericHits = GENERIC_PHRASES.filter((p) => lower.includes(p));
  const bulletTexts = diagnostics.bullets.map((b) => b.text);
  const isConcrete = (b: string) => {
    const body = b.replace(/^[-•*·▪●○]\s*/, "");
    const hasProperNoun = /(?:\s)[A-Z][A-Za-z&.'-]{2,}/.test(body.slice(1));
    const hasAcronym = /\b[A-Z]{2,6}\b/.test(body);
    const hasNumber = /\d/.test(body);
    return hasProperNoun || hasAcronym || hasNumber;
  };
  const vagueBullets = bulletTexts.filter((b) => !isConcrete(b));
  const openers = bulletTexts.map((b) =>
    b.replace(/^[-•*·▪●○]\s*/, "").split(/\s+/)[0]?.toLowerCase() || "",
  );
  const openerCounts = new Map<string, number>();
  for (const o of openers) if (o) openerCounts.set(o, (openerCounts.get(o) || 0) + 1);
  const repeatedOpeners = [...openerCounts.entries()].filter(([, n]) => n >= 3);
  const concreteRatio = bulletTexts.length
    ? 1 - vagueBullets.length / bulletTexts.length
    : 0.5;
  const specificityPartial = Math.max(
    0,
    Math.min(
      1,
      concreteRatio - genericHits.length * 0.12 - (repeatedOpeners.length ? 0.1 : 0),
    ),
  );
  add({
    id: "specificity",
    label: "Specific to this candidate — no template filler",
    passed: specificityPartial >= 0.85,
    partial: specificityPartial,
    weight: 8,
    severity: "high",
    category: "impact",
    detail:
      [
        genericHits.length
          ? `Generic phrasing: ${genericHits.slice(0, 4).join(", ")}`
          : null,
        vagueBullets.length
          ? `${vagueBullets.length}/${bulletTexts.length} bullets name nothing concrete (no system, statute, client, employer, tool or figure)`
          : null,
        repeatedOpeners.length
          ? `Repeated bullet openers: ${repeatedOpeners.map(([o, n]) => `${o} ×${n}`).join(", ")}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ") ||
      "Every bullet names something concrete — this reads as your CV, not a template.",
    fix: "Name the actual system, statute, court, client type, document, department, product or region in each bullet, and vary the opening verb. A sentence that could sit in a stranger's CV unchanged should be rewritten.",
  });


  /* ---------------- Score ---------------- */
  const totalWeight = checks.reduce((s, c) => s + c.weight, 0) || 1;
  const earned = checks.reduce((s, c) => s + c.weight * Math.max(0, Math.min(1, c.partial)), 0);
  const score = Math.round((earned / totalWeight) * 100);
  const band: AtsAnalysis["band"] =
    score >= 85 ? "excellent" : score >= 70 ? "good" : score >= 50 ? "fair" : "poor";

  const priorityFixes = checks
    .filter((c) => c.partial < 1 && c.fix)
    .map((c) => ({
      label: c.label,
      fix: c.fix!,
      points: Math.round(c.weight * (1 - c.partial)),
    }))
    .sort((a, b) => b.points - a.points)
    .slice(0, 8);

  const dimensions = buildDimensions(checks);
  const verdict = buildVerdict(score, priorityFixes);

  const warnings = [
    ...diagnostics.contact.warnings,
    ...diagnostics.risks.flags.map((f) => `${f.label}: ${f.detail}`),
    ...diagnostics.dates.formatIssues,
    ...diagnostics.dates.futureDates,
    ...diagnostics.dates.gaps,
    ...diagnostics.dates.overlaps,
    ...diagnostics.grammar.issues.map((i) => `${i.type}: ${i.detail}`),
  ];

  return {
    score,
    band,
    checks,
    wordCount,
    bulletCount: bulletBodies.length,
    missingKeywords: missing,
    matchedKeywords: matched,
    priorityFixes,
    dimensions,
    verdict,
    warnings,
    diagnostics,
  };
}

/* ------------------------------------------------------------------ */
/* Dimensions & verdict                                                */
/* ------------------------------------------------------------------ */

const DIMENSION_MAP: {
  id: AtsDimensionId;
  label: string;
  checkIds: string[];
  blurb: string;
}[] = [
  {
    id: "formatting",
    label: "Formatting",
    checkIds: ["parse", "risk", "sections", "length"],
    blurb: "Whether an ATS can extract your text cleanly and in the right order.",
  },
  {
    id: "keywords",
    label: "Keywords",
    checkIds: ["keywords", "skills"],
    blurb: "How closely your wording matches the target role and job advert.",
  },
  {
    id: "grammar",
    label: "Grammar",
    checkIds: ["grammar"],
    blurb: "Spelling, punctuation, capitalisation and sentence mechanics.",
  },
  {
    id: "readability",
    label: "Readability",
    checkIds: ["readability", "bullets"],
    blurb: "How fast a recruiter can skim the document and find what matters.",
  },
  {
    id: "achievements",
    label: "Achievements",
    checkIds: ["quantified", "verbs", "weak-language", "specificity"],
    blurb: "Whether your bullets prove outcomes instead of listing duties.",
  },
  {
    id: "recruiterAppeal",
    label: "Recruiter appeal",
    checkIds: ["summary", "verbs", "readability", "specificity"],
    blurb: "First-impression quality: a targeted summary and confident language.",
  },
  {
    id: "completeness",
    label: "Completeness",
    checkIds: ["completeness", "contact", "date-quality"],
    blurb: "Every section and field a screener expects to find is there.",
  },
  {
    id: "professionalism",
    label: "Professionalism",
    checkIds: ["tone", "popia", "contact-quality"],
    blurb: "Tone, privacy hygiene (POPIA) and professional contact details.",
  },
];

function buildDimensions(checks: AtsCheckResult[]): AtsDimension[] {
  const byId = new Map(checks.map((c) => [c.id, c]));
  return DIMENSION_MAP.map((d) => {
    const members = d.checkIds.map((id) => byId.get(id)).filter(Boolean) as AtsCheckResult[];
    const w = members.reduce((s, c) => s + c.weight, 0) || 1;
    const e = members.reduce((s, c) => s + c.weight * Math.max(0, Math.min(1, c.partial)), 0);
    return {
      id: d.id,
      label: d.label,
      score: Math.round((e / w) * 100),
      checkIds: members.map((c) => c.id),
      blurb: d.blurb,
    };
  });
}

function buildVerdict(
  score: number,
  fixes: { label: string; fix: string; points: number }[],
): AtsVerdict {
  const topActions = fixes.slice(0, 3).map((f) => f.fix);
  if (score >= 90)
    return {
      band: "excellent",
      label: "Excellent",
      explanation:
        "This CV parses cleanly, mirrors the target role's language and proves outcomes. It should pass automated screening and read well to a human recruiter. Keep it tailored per application.",
      topActions,
    };
  if (score >= 75)
    return {
      band: "good",
      label: "Good",
      explanation:
        "This CV will get through most Applicant Tracking Systems, but it is losing points a competitor could pick up — usually keyword coverage or quantified results. The fixes below are the highest-value ones.",
      topActions,
    };
  if (score >= 60)
    return {
      band: "needs-improvement",
      label: "Needs improvement",
      explanation:
        "Parts of this CV will parse, but screening filters and recruiters will find gaps — missing sections, weak achievement language or thin keyword coverage. Work through the priority fixes before applying.",
      topActions,
    };
  return {
    band: "high-risk",
    label: "High risk",
    explanation:
      "This CV is likely to be filtered out before a human sees it. The structure, contact block or text layer needs rebuilding before you apply. Start with the critical fixes below.",
    topActions,
  };
}


/* ------------------------------------------------------------------ */
/* Fact-integrity guard                                                */
/* ------------------------------------------------------------------ */

export type FactIntegrityReport = {
  ok: boolean;
  /** Numbers/percentages present in the rewrite but absent from the source. */
  inventedFigures: string[];
  /** Capitalised multi-word entities (employers, institutions) not in source. */
  inventedEntities: string[];
};

const ENTITY_STOP = new Set([
  "CONTACT","PROFESSIONAL","SUMMARY","CORE","SKILLS","EXPERIENCE","EDUCATION",
  "CERTIFICATIONS","REFERENCES","ACHIEVEMENTS","LANGUAGES","PROJECTS","PRESENT",
  "CURRENT","AVAILABLE","REQUEST","JANUARY","FEBRUARY","MARCH","APRIL","MAY",
  "JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER",
]);

function numbersIn(s: string): string[] {
  return (s.match(/\b\d[\d\s,.]*%?\b/g) || [])
    .map((n) => n.replace(/[\s,]/g, "").replace(/\.$/, ""))
    .filter((n) => n.length > 0);
}

/**
 * Compares a rewritten CV against its source and flags anything that looks
 * fabricated. Purely mechanical — used to keep AI rewrites truthful.
 */
export function checkFactIntegrity(source: string, rewrite: string): FactIntegrityReport {
  const srcNums = new Set(numbersIn(source));
  const inventedFigures = Array.from(new Set(numbersIn(rewrite))).filter(
    (n) => !srcNums.has(n) && !/^(19|20)\d{2}$/.test(n) && n.replace("%", "").length > 0 && Number(n.replace("%", "")) > 1,
  );

  const srcLower = source.toLowerCase();
  const entities = Array.from(
    new Set(
      (rewrite.match(/\b([A-Z][a-zA-Z&.]+(?:\s+[A-Z][a-zA-Z&.]+){1,3})\b/g) || []).filter(
        (e) => !e.split(/\s+/).every((w) => ENTITY_STOP.has(w.toUpperCase())),
      ),
    ),
  );
  const inventedEntities = entities.filter((e) => !srcLower.includes(e.toLowerCase())).slice(0, 20);

  return {
    ok: inventedFigures.length === 0 && inventedEntities.length === 0,
    inventedFigures: inventedFigures.slice(0, 20),
    inventedEntities,
  };
}
